import dynamic from 'next/dynamic';
import useCommunityCookieProperties from '@aurora/shared-client/components/community/useCommunityCookieProperties';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import React, { useContext, useEffect, useState } from 'react';
import type { GdprCookies } from '@aurora/shared-client/helpers/cookie/CookieHelper';
import {
  fetchGdprCookies,
  GdprCookiePreferences,
  GdprCookieValues,
  LITHIUM_NECESSARY,
  setGdprCookies
} from '@aurora/shared-client/helpers/cookie/CookieHelper';
import useFloatingBox from '@aurora/shared-client/components/context/FloatingBoxContext/useFloatingBox';

const CookiePreferencesModal = dynamic(
  () => import('./CookiePreferencesModal/CookiePreferencesModal'),
  { ssr: false }
);

const CookieBannerFloatingBox = dynamic(
  () => import('../CookieBannerFloatingBox/CookieBannerFloatingBox'),
  { ssr: false }
);

export const COOKIE_BANNER_FLOATING_BOX_ID = 'cookieBannerBox';

/**
 * Cookie Banner.
 *
 * @author Rishabh Jivan, Adamn Ayres
 */
const CookieBanner: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { data, loading } = useCommunityCookieProperties(module);
  const { addBox, removeBox, floatingBox, showBox } = useFloatingBox(COOKIE_BANNER_FLOATING_BOX_ID);

  const {
    publicConfig: { trafficVisitorCookieMaxAge }
  } = useContext(TenantContext);
  const [showModal, setShowModal] = useState<boolean>(false);

  const { cookiePrivacyPolicyUrl } = data?.community?.cookieProperties ?? {};

  useEffect(() => {
    if (!loading) {
      const gdprCookies: GdprCookies = fetchGdprCookies();
      const { isCookieBannerEnabled } = data?.community?.cookieProperties ?? {};
      if (isCookieBannerEnabled && !gdprCookies.userPreferenceExist) {
        addBox();
      }
    }
  }, [data, loading, addBox, cookiePrivacyPolicyUrl]);

  if (loading) {
    return null;
  }

  /**
   * Function that is called when the submit button is clicked from the preferences modal
   *
   * @param result Object containing the user's selected cookie preferences
   */
  const onPreferenceSubmitted = result => {
    const gdprCookies: GdprCookies = fetchGdprCookies();
    const cookiePreferences: GdprCookiePreferences = new GdprCookiePreferences();
    for (const key of Object.keys(gdprCookies.preferences)) {
      if (key === LITHIUM_NECESSARY || result.preferences[key]) {
        cookiePreferences[key] = GdprCookieValues.ACCEPTED;
      } else {
        cookiePreferences[key] = GdprCookieValues.REJECTED;
      }
    }
    setGdprCookies(cookiePreferences, trafficVisitorCookieMaxAge);
    setShowModal(false);
    removeBox();
  };

  return (
    <>
      <CookiePreferencesModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmitted={onPreferenceSubmitted}
        cookiePrivacyPolicyUrl={cookiePrivacyPolicyUrl}
      />
      {showBox &&
        floatingBox(
          <CookieBannerFloatingBox
            onShowModal={show => setShowModal(show)}
            cookiePrivacyPolicyUrl={cookiePrivacyPolicyUrl}
          />
        )}
    </>
  );
};

export default CookieBanner;
